import { ChevronsRight } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { DrawerHeader, Inline, Stack, Typography } from '@crossnokaye/ux-common';
import { Button } from '@crossnokaye/ux-common/ckds';

import { PreviewItem } from '@/components/settings/preview-item';
import { PreviewSection } from '@/components/settings/preview-section';
import type { SettingChange, SettingChangeGroup } from '@/components/settings/settings-save.types';

import { type GroupBy, GroupByToggle } from '../components/group-by-toggle';
import {
  discardFormCell,
  groupChangesByEquipment,
  groupChangesBySetting,
} from '../equipment-bulk-edit.helpers';
import type {
  BulkEditChangeMetadata,
  EquipmentBulkEditFormValues,
} from '../equipment-bulk-edit.types';

interface EquipmentBulkEditPreviewPushoutProps {
  onClose: () => void;
  settingChanges: SettingChange[];
}

/**
 * EquipmentBulkEditPreviewPushout
 *
 * Preview pushout showing unsaved changes grouped by Equipment or Setting.
 * Each change can be individually discarded via a trash icon.
 */
export const EquipmentBulkEditPreviewPushout = ({
  onClose,
  settingChanges,
}: EquipmentBulkEditPreviewPushoutProps): React.ReactNode => {
  const [groupBy, setGroupBy] = useState<GroupBy>('equipment');

  const {
    setValue,
    formState: { defaultValues },
  } = useFormContext<{ values: EquipmentBulkEditFormValues }>();

  const groups: SettingChangeGroup[] =
    groupBy === 'equipment'
      ? groupChangesByEquipment(settingChanges)
      : groupChangesBySetting(settingChanges);

  const handleDiscard = useCallback(
    (rowId: string, columnId: string) => {
      discardFormCell(setValue, defaultValues, rowId, columnId);
    },
    [defaultValues, setValue]
  );

  return (
    <div
      className="h-full flex flex-col border-l border-t border-ckds-border-disabled"
      data-component="EquipmentBulkEditPreviewPushout"
    >
      {/* Header */}
      <DrawerHeader className="py-4 px-5 border-b border-ckds-border-tier2">
        <Stack gap="default">
          <Inline gap="default" className="items-center">
            <Button
              variant="tier2"
              size="md"
              shape="square"
              onClick={onClose}
              leftIcon={ChevronsRight}
              aria-label="Close preview"
            />
            <Typography variant="h2">Preview Unsaved Changes</Typography>
          </Inline>

          <GroupByToggle value={groupBy} onChange={setGroupBy} />
        </Stack>
      </DrawerHeader>

      {/* Content Area */}
      <div className="flex-1 overflow-auto p-5">
        {settingChanges.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <Typography variant="body" className="text-secondary">
              No unsaved changes
            </Typography>
          </div>
        ) : (
          <Stack gap="default">
            {groups.map((group) => (
              <PreviewSection key={group.id} title={group.label}>
                <Stack gap="thin">
                  {group.changes.map((change) => {
                    const meta = change.metadata as unknown as BulkEditChangeMetadata;
                    const label = groupBy === 'equipment' ? meta.columnLabel : meta.rowName;
                    return (
                      <PreviewItem
                        key={change.changeId}
                        item={{
                          id: change.changeId,
                          label,
                          displayCurrent: change.displayCurrent,
                          displayOriginal: change.displayOriginal,
                        }}
                        onDiscard={() => handleDiscard(meta.rowId, meta.columnId)}
                      />
                    );
                  })}
                </Stack>
              </PreviewSection>
            ))}
          </Stack>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-ckds-border-disabled p-stack-sm flex justify-stretch w-full">
        <Button variant="tier2" onClick={onClose} className="flex-1">
          Close
        </Button>
      </div>
    </div>
  );
};
